// frontend/src/pages/Lesson.tsx
// is_premium ve is_preview flag'lerine göre erişim kontrolü eklendi.
// useFeatures() hook'u ile DB'den gelen plan izinlerine bakılır.

import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { coursesService } from '../services/courses.service';
import { supabase } from '../lib/supabaseClient';
import { Course, Lesson, LessonTopic, UnderstandingFeedback } from '../types/courses.types';
import { UnderstandingFeedbackCard } from '../components/lesson/UnderstandingFeedback';
import { AIExplanationCard } from '../components/lesson/AIExplanationCard';
import { MiniCodeExercise } from '../components/lesson/MiniCodeExercise';
import { CinAliPlayer } from '../components/lesson/CinAliPlayer';
import { FeatureGate } from '../components/common/FeatureGate';
import { useFeatures } from '../hooks/useFeatures';
import { useAuthStore } from '../store/authStore';
import { usePlanUsage } from '../hooks/usePlanUsage';
import { UsageBanner } from '../components/common/UsageBanner';
import { PlanLimitBar } from '../components/common/PlanLimitBar';
import { stopGlobalTTS } from '../utils/speechQueueManager';
import { VideoEmbedPlayer } from '../components/common/VideoEmbedPlayer';
import '../styles/lesson.css';
import '../styles/cinali.css';

export function LessonPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const fetchProfile = useAuthStore((s) => s.fetchProfile);
  const { hasFeature } = useFeatures();

  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [topics, setTopics] = useState<LessonTopic[]>([]);
  const [course, setCourse] = useState<Course | null>(null);
  const [allLessons, setAllLessons] = useState<Lesson[]>([]); // Kursa ait tüm dersler
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [existingPdfNote, setExistingPdfNote] = useState<string | undefined>(undefined);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteSaveStatus, setNoteSaveStatus] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [completionResult, setCompletionResult] = useState<{ xpAwarded: number; newCourseProgressPercent?: number; courseCompleted?: boolean } | null>(null);
  const [isLessonCompleted, setIsLessonCompleted] = useState(false);
  const [activeTopicVideoId, setActiveTopicVideoId] = useState<string | null>(null);
  const [courseProgressPercent, setCourseProgressPercent] = useState<number>(0);
  const { isAtLimit, isNearLimit, usage, refetch: refetchUsage } = usePlanUsage();
  // Ders tamamlama limiti: daha önce tamamlanmış bir dersin tekrar tamamlanması engellenmez
  // (her ders 1 kez tamamlanabilir zaten), ancak günlük YENI ders limiti uygulanır.
  const atLimit = !isLessonCompleted && isAtLimit('lesson');

  useEffect(() => {
    if (!lessonId) return;
    let cancelled = false;

    // Reset view states
    setNotes('');
    setExistingPdfNote(undefined);
    setNoteSaveStatus(null);
    setCompletionResult(null);
    setIsLessonCompleted(false);
    setCourseProgressPercent(0);

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const lessonData = await coursesService.getLesson(lessonId!);
        if (cancelled) return;
        if (!lessonData) { setError('Ders bulunamadı.'); return; }
        setLesson(lessonData);

        const topicsData = await coursesService.listLessonTopics(lessonId!);
        let finalTopics = topicsData ?? [];

        // Fallback: Eğer veritabanında henüz özel konu satırları yoksa, ders içeriğindeki başlıkları çek
        if (finalTopics.length === 0 && lessonData.content) {
          const headers = lessonData.content.match(/^#{2,3}\s+(.+)$/gm);
          if (headers && headers.length > 0) {
            finalTopics = headers.map((h, idx) => ({
              id: `auto-${idx}`,
              lesson_id: lessonData.id,
              title: h.replace(/^#{2,3}\s+/, '').trim(),
              description: null,
              video_url: null,
              is_premium: false,
              order_index: idx + 1,
              created_at: new Date().toISOString(),
            }));
          }
        }

        if (!cancelled) setTopics(finalTopics);

        const courseData = await coursesService.getCourse(lessonData.course_id);
        if (!cancelled) setCourse(courseData);

        // Kursa ait tüm dersleri yükle (sıradaki dersi bulmak için)
        const allLessonsData = await coursesService.listLessons(lessonData.course_id);
        if (!cancelled) setAllLessons(allLessonsData);

        // Check if this lesson is already completed and load notes
        if (profile) {
          const [completionRes, noteRes, completionsAllRes] = await Promise.all([
            supabase
              .from('student_lesson_completions')
              .select('lesson_id')
              .eq('student_id', profile.id)
              .eq('lesson_id', lessonId!)
              .maybeSingle(),
            supabase
              .from('student_notes')
              .select('user_note, pdf_note')
              .eq('student_id', profile.id)
              .eq('lesson_id', lessonId!)
              .maybeSingle(),
            supabase
              .from('student_lesson_completions')
              .select('lesson_id')
              .eq('student_id', profile.id)
              .in('lesson_id', allLessonsData.map(l => l.id))
          ]);
          
          if (!cancelled && completionRes.data) setIsLessonCompleted(true);
          if (!cancelled && noteRes.data) {
            setNotes(noteRes.data.user_note || '');
            setExistingPdfNote(noteRes.data.pdf_note || undefined);
          }
          if (!cancelled && completionsAllRes.data) {
            const completedCount = completionsAllRes.data.length;
            const totalCount = allLessonsData.length;
            setCourseProgressPercent(totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Ders yüklenemedi.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [lessonId, profile?.id]);

  // Sayfa unmount olduğunda (başka sayfaya gidilince) sesi kes
  useEffect(() => {
    return () => {
      stopGlobalTTS();
    };
  }, []);

  async function handleCompleteLesson() {
    if (!lesson) return;
    setIsCompleting(true);
    setCompletionError(null);
    try {
      const result = await coursesService.completeLesson(lesson.id);
      setCompletionResult(result);
      setIsLessonCompleted(true);
      if (result && typeof result.newCourseProgressPercent === 'number') {
        setCourseProgressPercent(result.newCourseProgressPercent);
      }
      await fetchProfile();
      refetchUsage(); // Güncel limit durumunu yeniden yükle
    } catch (err) {
      setCompletionError(err instanceof Error ? err.message : 'Ders tamamlanamadı.');
    } finally {
      setIsCompleting(false);
    }
  }

  async function handleSaveLessonNote() {
    if (!lesson || !course) return;
    try {
      setIsSavingNote(true);
      setNoteSaveStatus(null);
      await coursesService.saveNote({
        courseId: course.id,
        lessonId: lesson.id,
        userNote: notes,
        pdfNote: existingPdfNote
      });
      setNoteSaveStatus('Not başarıyla kaydedildi! 💾');
      setTimeout(() => setNoteSaveStatus(null), 3000);
    } catch (err) {
      console.error('Not kaydedilemedi:', err);
      setNoteSaveStatus('Not kaydedilirken hata oluştu.');
      setTimeout(() => setNoteSaveStatus(null), 3000);
    } finally {
      setIsSavingNote(false);
    }
  }

  if (isLoading) return <div className="courses-loading">Yükleniyor...</div>;
  if (error) return <div className="courses-error">{error}</div>;
  if (!lesson || !course) return null;

  // ── Erişim kontrolü ────────────────────────────────────────────
  const lessonAny = lesson as any;
  const isPremiumLesson = lessonAny.is_premium === true;
  const isPreview = lessonAny.is_preview === true;
  const isAdmin = profile?.role === 'admin';
  const canAccess = isAdmin || isPreview || !isPremiumLesson || hasFeature('lesson.premium') || hasFeature('course.premium');

  // Kilitli ders: içerik yerine yükseltme ekranı göster (yalnızca ücretsiz kullanıcılar için)
  if (!canAccess) {
    return (
      <div className="lesson-page">
        <div className="lesson-top-bar">
          <div>
            <div className="lesson-badges"><span className="lesson-badge primary">{course.title}</span></div>
            <h1>{lesson.title}</h1>
          </div>
          <Link to={`/courses/${course.id}`} className="lesson-btn secondary">⬅️ Derslere Dön</Link>
        </div>

        <div className="card" style={{ textAlign: 'center', padding: '64px 32px', marginTop: 32 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🔒</div>
          <h2 style={{ marginBottom: 8 }}>Bu Ders Premium'a Özel</h2>
          <p style={{ color: 'var(--gray)', marginBottom: 24 }}>
            Bu dersi görüntülemek ve sınırsız kodlama yapmak için Pro veya Premium planına geçmen gerekiyor.
          </p>
          <Link to="/pricing" className="lesson-btn primary" style={{ display: 'inline-block' }}>
            💎 Planları İncele
          </Link>
        </div>
      </div>
    );
  }

  // ── Günlük ders limiti dolmuşsa kilit ekranı göster ──────────────────
  if (atLimit && usage) {
    return (
      <div className="lesson-page">
        <div className="lesson-top-bar">
          <div>
            <div className="lesson-badges"><span className="lesson-badge primary">{course.title}</span></div>
            <h1>{lesson.title}</h1>
          </div>
          <Link to={`/courses/${course.id}`} className="lesson-btn secondary">⬅️ Derslere Dön</Link>
        </div>

        <div className="card" style={{ textAlign: 'center', padding: '48px 32px', marginTop: 32 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🚫</div>
          <h2 style={{ marginBottom: 8, color: '#ef4444' }}>Günlük Ders Limitine Ulaştın</h2>
          <p style={{ color: 'var(--gray)', marginBottom: 8 }}>
            <strong>{usage.planName}</strong> planında günlük en fazla <strong>{usage.lessonsLimit}</strong> ders tamamlayabilirsin.
          </p>
          <p style={{ color: 'var(--gray)', fontSize: 13, marginBottom: 24 }}>
            🔄 Limit yarın gece yarısı (UTC) sıfırlanır. Daha fazlası için planını yükselt.
          </p>
          <div style={{ maxWidth: 360, margin: '0 auto 24px' }}>
            <PlanLimitBar show={['lesson']} />
          </div>
          <Link to="/pricing" className="lesson-btn primary" style={{ display: 'inline-block' }}>
            💎 Planı Yükselt
          </Link>
        </div>
      </div>
    );
  }

  // ── Normal ders içeriği ─────────────────────────────────────────────
  // Sıradaki derse geçiş: allLessons order_index'e göre sıralıdır
  const sortedLessons = [...allLessons].sort((a, b) => a.order_index - b.order_index);
  const currentLessonIdx = sortedLessons.findIndex((l) => l.id === lesson.id);
  const nextLesson = currentLessonIdx >= 0 && currentLessonIdx < sortedLessons.length - 1
    ? sortedLessons[currentLessonIdx + 1]
    : null;

  // Kurs geneli tamamlanma yüzdesini tamamlanma sonrası göster
  const currentProgressPercent = completionResult?.newCourseProgressPercent ?? null;
  const courseJustCompleted = completionResult?.courseCompleted === true;
  function getDifficultyLabel(diff?: string) {
    switch (diff) {
      case 'beginner': return 'BAŞLANGIÇ';
      case 'intermediate': return 'ORTA SEVİYE';
      case 'advanced': return 'İLERİ SEVİYE';
      default: return 'BAŞLANGIÇ';
    }
  }

  return (
    <div className="lesson-page">
      <div className="lesson-breadcrumb">
        <Link to="/courses">Dersler</Link>
        <span className="lesson-breadcrumb-sep">/</span>
        <Link to={`/courses/${course.id}`}>{course.title}</Link>
        <span className="lesson-breadcrumb-sep">/</span>
        <span>{lesson.title}</span>
      </div>

      {/* Redesigned Upper Header Card */}
      <div className="new-lesson-header-card">
        <div className="new-lesson-header-tags">
          <span className="header-tag category-tag">
            {course.category ? course.category.toUpperCase() : 'PYTHON TEMELLERİ'}
          </span>
          <span className="header-tag difficulty-tag">
            {getDifficultyLabel(course.difficulty)}
          </span>
          <span className="header-tag progress-tag">
            %{courseProgressPercent} TAMAMLANDI
          </span>
        </div>
        
        <div className="new-lesson-header-main">
          <div className="new-lesson-header-text">
            <h1>{lesson.title}</h1>
            <p>{lesson.content ? lesson.content.substring(0, 160).replace(/#/g, '') + '...' : course.description}</p>
            
            <div className="new-lesson-meta-info">
              <span>📑 {topics.length > 0 ? topics.length : 8} slayt</span>
              <span>⏱️ ~20 dk</span>
              <span>🎮 {topics.length > 0 ? topics.length : 3} alıştırma</span>
              <span>🏆 {lesson.xp_reward || 150} XP</span>
            </div>
          </div>
          
          <div className="new-lesson-header-actions">
            <Link to={`/courses/${course.id}`} className="new-lesson-action-btn back-btn">
              <span>⬅️</span> Geri
            </Link>
            {nextLesson ? (
              <Link to={`/lessons/${nextLesson.id}`} className="new-lesson-action-btn next-btn">
                Sonraki Ders ➡️
              </Link>
            ) : (
              <button className="new-lesson-action-btn next-btn" onClick={handleCompleteLesson} disabled={isCompleting || isLessonCompleted}>
                {isLessonCompleted ? 'Tamamlandı ✓' : 'Dersi Tamamla ✓'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar below the header */}
      <div className="new-lesson-progress-row">
        <span className="progress-title">Ders İlerlemesi</span>
        <div className="progress-bar-container">
          <div className="progress-bar-fill-gradient" style={{ width: `${courseProgressPercent}%` }} />
        </div>
        <span className="progress-percentage-label">%{courseProgressPercent}</span>
      </div>

      <UsageBanner type="lesson" />

      {/* Unified Player containing left Board and right Sidebar columns */}
      <CinAliPlayer 
        lesson={{ ...lesson, topics }} 
        course={course} 
        onCompleteLesson={handleCompleteLesson}
        isLessonCompleted={isLessonCompleted || !!completionResult}
        courseProgressPercent={courseProgressPercent}
        nextLesson={nextLesson}
        notes={notes}
        setNotes={setNotes}
        onSaveNote={handleSaveLessonNote}
        isSavingNote={isSavingNote}
        noteSaveStatus={noteSaveStatus}
        completionResult={completionResult}
        topics={topics}
        activeTopicVideoId={activeTopicVideoId}
        setActiveTopicVideoId={setActiveTopicVideoId}
      />

      {lesson.video_url && (
        <div className="card lesson-extra-resource" style={{ marginTop: 24, padding: 28 }}>
          <h3 style={{ marginBottom: 16, color: 'var(--text-primary)', fontSize: 18, fontWeight: 800 }}>📚 Ek Kaynak Video</h3>
          <div style={{ maxWidth: 840, margin: '0 auto' }}>
            <VideoEmbedPlayer url={lesson.video_url} title={lesson.title} />
          </div>
        </div>
      )}
    </div>
  );
}
